<template>
  <div class="auth-button-container">
    <!-- Sign In Button (when not authenticated) -->
    <button
      v-if="!user"
      @click="handleSignIn"
      :disabled="isLoading"
      class="sign-in-button"
    >
      <svg viewBox="0 0 24 24" class="google-icon">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      <span v-if="!isLoading">Sign in to rate tracks</span>
      <span v-else>Signing in...</span>
    </button>

    <!-- User Info (when authenticated) -->
    <div v-else class="user-info">
      <img
        v-if="user.photoURL"
        :src="user.photoURL"
        :alt="user.displayName || 'User'"
        class="user-avatar"
      />
      <div v-else class="user-avatar-placeholder">
        {{ (user.displayName || user.email || 'U').charAt(0).toUpperCase() }}
      </div>

      <span class="user-name">{{ user.displayName || user.email || 'User' }}</span>

      <button
        @click="handleSignOut"
        class="sign-out-button"
        title="Sign out"
      >
        Sign Out
      </button>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { signInWithGoogle, signOutUser, onAuthChange } from '../utils/authUtils'
import { migrateLocalRatingsToFirebase } from '../utils/ratingUtils'

export default {
  name: 'AuthButton',

  emits: ['auth-change'],

  setup (props, { emit }) {
    const user = ref(null)
    const isLoading = ref(false)
    const errorMessage = ref('')
    let authUnsubscribe = null

    /**
     * Handle sign-in button click
     */
    const handleSignIn = async () => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        const result = await signInWithGoogle()

        if (result.success) {
          console.log('✓ Signed in successfully:', result.user.displayName || result.user.email)

          // Migrate any local ratings to Firebase
          setTimeout(async () => {
            try {
              const migrationResult = await migrateLocalRatingsToFirebase()
              if (migrationResult.migrated > 0) {
                console.log(`✓ Migrated ${migrationResult.migrated} ratings to your account`)
              }
            } catch (error) {
              console.error('Migration error:', error)
            }
          }, 1000)
        } else {
          errorMessage.value = result.error
          console.error('Sign-in failed:', result.error)
        }
      } catch (error) {
        errorMessage.value = 'An unexpected error occurred'
        console.error('Sign-in error:', error)
      } finally {
        isLoading.value = false
      }
    }

    /**
     * Handle sign-out button click
     */
    const handleSignOut = async () => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        const result = await signOutUser()

        if (result.success) {
          console.log('✓ Signed out successfully')
        } else {
          errorMessage.value = result.error
          console.error('Sign-out failed:', result.error)
        }
      } catch (error) {
        errorMessage.value = 'An unexpected error occurred'
        console.error('Sign-out error:', error)
      } finally {
        isLoading.value = false
      }
    }

    /**
     * Set up auth state listener
     */
    onMounted(() => {
      // Listen for authentication state changes
      authUnsubscribe = onAuthChange((firebaseUser) => {
        user.value = firebaseUser

        // Emit event to parent components
        emit('auth-change', firebaseUser)

        if (firebaseUser) {
          console.log('Auth state: Signed in as', firebaseUser.displayName || firebaseUser.email)
        } else {
          console.log('Auth state: Signed out')
        }
      })
    })

    /**
     * Clean up listener on unmount
     */
    onUnmounted(() => {
      if (authUnsubscribe) {
        authUnsubscribe()
      }
    })

    return {
      user,
      isLoading,
      errorMessage,
      handleSignIn,
      handleSignOut
    }
  }
}
</script>

<style scoped>
.auth-button-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Sign In Button */
.sign-in-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: white;
  color: #444;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.sign-in-button:hover:not(:disabled) {
  background: #f8f8f8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sign-in-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.google-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* User Info */
.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.user-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-primary, #66b2ff);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.user-name {
  font-size: 0.95rem;
  color: white;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sign-out-button {
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.sign-out-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Error Message */
.error-message {
  font-size: 0.85rem;
  color: #ff6b6b;
  padding: 0.5rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(255, 107, 107, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .sign-in-button {
    font-size: 0.9rem;
    padding: 0.65rem 1.25rem;
  }

  .user-name {
    max-width: 100px;
  }

  .sign-out-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .sign-in-button {
    font-size: 0.85rem;
    padding: 0.6rem 1rem;
  }

  .google-icon {
    width: 18px;
    height: 18px;
  }

  .user-info {
    gap: 0.5rem;
  }

  .user-avatar,
  .user-avatar-placeholder {
    width: 28px;
    height: 28px;
  }

  .user-name {
    font-size: 0.85rem;
    max-width: 80px;
  }
}
</style>
